import { useState, type FormEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Check } from 'lucide-react';
import { ThemeToggle } from '../components/theme/ThemeToggle';
import { CLEAN_CODE_CULTURE_URL } from '../components/brand/CleanCodeCultureLogo';
import { useAuthStore } from '../store/useAuthStore';
import { useI18n } from '../contexts/I18nContext';
import { ApiError } from '../lib/api';
import * as authApi from '../lib/authApi';

interface LocationState {
  from?: { pathname?: string; search?: string };
}

export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const loginAction = useAuthStore((s) => s.login);
  const loginDemo = useAuthStore((s) => s.loginDemo);
  const { t } = useI18n();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  // EMAIL_NOT_VERIFIED flow: offer to resend the verification email.
  const [needsVerification, setNeedsVerification] = useState(false);
  const [verificationSent, setVerificationSent] = useState(false);

  // Where the user came from before being bounced to login (deep-link
  // protection). When present we honor it so a user who tried to open a
  // specific protected page lands back there after authenticating.
  // The query string rides along: /oauth/consent?req=… must survive the login.
  const fromState = (location.state as LocationState | null)?.from;
  const from = fromState?.pathname ? `${fromState.pathname}${fromState.search ?? ''}` : null;

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setNeedsVerification(false);
    setVerificationSent(false);
    setSubmitting(true);
    try {
      await loginAction(email, password);
      // Every user lands on the daily summary after login (CLE-448). A
      // pending deep-link (`from`) always wins.
      navigate(from ?? authApi.postAuthLandingPath(), { replace: true });
    } catch (err) {
      if (err instanceof ApiError && err.code === 'EMAIL_NOT_VERIFIED') {
        setError(t('login.emailNotVerified'));
        setNeedsVerification(true);
      } else {
        setError(err instanceof Error ? err.message : t('login.failed'));
      }
    } finally {
      setSubmitting(false);
    }
  }

  async function handleResendVerification() {
    setSubmitting(true);
    try {
      await authApi.resendVerification(email);
      setVerificationSent(true);
    } finally {
      setSubmitting(false);
    }
  }

  async function handleDemo() {
    setError(null);
    setSubmitting(true);
    try {
      await loginDemo();
      navigate(authApi.postAuthLandingPath(), { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : t('login.demoUnavailable'));
    } finally {
      setSubmitting(false);
    }
  }

  const features = [t('login.feature.1'), t('login.feature.2'), t('login.feature.3')];

  // Mirrors the integrations advertised on the firmio.ro marketing site, in the
  // same order, so the two pages don't claim different things.
  const integrations = [
    'Google Drive',
    'Google Calendar',
    'e-Factura ANAF',
    'Anthropic Claude',
    'Oblio',
    'Linear',
    'Asana',
    'Jira',
    'SMTP / Email',
    'SharePoint',
  ];

  return (
    <div className="relative flex min-h-screen overflow-hidden" style={{ background: '#0A0E1A' }}>
      {/* Dual radial gradient blobs — top-left blue + bottom-right purple. */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            'radial-gradient(ellipse 1100px 700px at 15% 20%, rgba(59, 130, 246, 0.15), transparent 70%)',
        }}
      />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            'radial-gradient(ellipse 900px 900px at 85% 80%, rgba(139, 92, 246, 0.12), transparent 70%)',
        }}
      />

      {/* Dot-pattern overlay. */}
      <div aria-hidden className="auth-dots pointer-events-none absolute inset-0" />

      <div className="absolute right-4 top-4 z-10">
        <ThemeToggle />
      </div>

      {/* Split layout: brand panel (left) + form panel (right) on lg+, stacked on mobile. */}
      <div className="relative z-10 flex w-full flex-col items-center justify-center p-4 lg:flex-row lg:items-stretch lg:p-0">
        {/* ── Left panel: brand showcase ─────────────────────────────── */}
        <div className="flex w-full max-w-lg flex-col justify-center px-4 py-8 lg:w-[55%] lg:max-w-none lg:px-16 lg:py-16 xl:px-24">
          {/* The official Firmio wordmark ("firmio." with the purple dot). */}
          <h1 className="-ml-3">
            <img
              src="/brand/firmio-logo-horizontal-on-dark.svg"
              alt="Firmio"
              className="h-20 w-auto lg:h-24"
            />
          </h1>

          {/* Tagline */}
          <p
            className="mt-4 text-xl font-semibold lg:text-2xl"
            style={{ color: '#F1F5F9', fontFamily: "'Bricolage Grotesque', sans-serif" }}
          >
            {t('login.tagline')}
          </p>

          {/* Subtitle */}
          <p className="mt-2 text-sm lg:text-base" style={{ color: '#94A3B8' }}>
            {t('login.subtitle')}
          </p>

          {/* Feature highlights — hidden on mobile */}
          <div className="mt-8 hidden space-y-3 lg:block">
            {features.map((feat) => (
              <div key={feat} className="flex items-center gap-3">
                <div
                  className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full"
                  style={{ background: 'rgba(59, 130, 246, 0.15)' }}
                >
                  <Check className="h-3.5 w-3.5" style={{ color: '#60A5FA' }} />
                </div>
                <span className="text-sm" style={{ color: '#CBD5E1' }}>
                  {feat}
                </span>
              </div>
            ))}
          </div>

          {/* Integrations strip — hidden on mobile */}
          <div className="mt-10 hidden lg:block">
            <p
              className="text-xs font-medium uppercase tracking-wider"
              style={{ color: '#64748B' }}
            >
              {t('login.integrations')}
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              {integrations.map((name) => (
                <span
                  key={name}
                  className="rounded-md px-3 py-1.5 text-xs font-medium"
                  style={{
                    color: '#94A3B8',
                    border: '1px solid rgba(148, 163, 184, 0.15)',
                    background: 'rgba(15, 22, 38, 0.6)',
                  }}
                >
                  {name}
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* ── Right panel: login form card ───────────────────────────── */}
        <div className="flex w-full max-w-sm items-center justify-center px-4 py-8 lg:w-[45%] lg:max-w-none lg:px-12 xl:px-20">
          <div className="w-full max-w-sm animate-[ui-fade-in_400ms_ease-out]">
            {/* Mobile-only: the Firmio wordmark above the card (desktop shows
                it on the left brand panel). */}
            <div className="mb-6 flex flex-col items-center gap-4 lg:hidden">
              <h1>
                <img
                  src="/brand/firmio-logo-horizontal-on-dark.svg"
                  alt="Firmio"
                  className="h-16 w-auto"
                />
              </h1>
            </div>

            <form
              onSubmit={handleSubmit}
              className="space-y-4 rounded-2xl p-6"
              style={{
                background: '#0F1626',
                border: '1px solid rgba(148, 163, 184, 0.12)',
                boxShadow: '0 20px 50px -15px rgba(99, 102, 241, 0.3)',
              }}
            >
              <h2
                className="text-2xl font-bold"
                style={{ color: '#F1F5F9', fontFamily: "'Bricolage Grotesque', sans-serif" }}
              >
                {t('login.title')}
              </h2>

              {error && (
                <p
                  className="rounded-[10px] px-3 py-2 text-sm"
                  role="alert"
                  style={{
                    background: 'rgba(248, 113, 113, 0.12)',
                    color: '#fca5a5',
                    border: '1px solid rgba(248, 113, 113, 0.25)',
                  }}
                >
                  {error}
                </p>
              )}

              {needsVerification &&
                (verificationSent ? (
                  <p className="text-sm" style={{ color: '#86efac' }}>
                    {t('login.verificationSent')}
                  </p>
                ) : (
                  <button
                    type="button"
                    onClick={handleResendVerification}
                    disabled={submitting}
                    className="text-sm underline disabled:opacity-50"
                    style={{ color: '#A78BFA' }}
                  >
                    {t('login.resendVerification')}
                  </button>
                ))}

              <label className="block text-sm">
                <span style={{ color: '#94A3B8' }}>{t('login.email')}</span>
                <input
                  type="email"
                  required
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="login-field mt-1 w-full px-3 py-2 outline-none"
                />
              </label>

              <label className="block text-sm">
                <span style={{ color: '#94A3B8' }}>{t('login.password')}</span>
                <input
                  type="password"
                  required
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="login-field mt-1 w-full px-3 py-2 outline-none"
                />
              </label>

              <p className="text-right text-sm">
                <Link to="/forgot-password" style={{ color: '#A78BFA' }} className="underline">
                  {t('login.forgotPassword')}
                </Link>
              </p>

              <button
                type="submit"
                disabled={submitting}
                className="login-submit w-full px-4 py-2.5 text-sm font-semibold text-white disabled:opacity-50"
              >
                {submitting ? t('login.submitting') : t('login.submit')}
              </button>

              {/* Enter Demo / Test Mode — frontend-only synthetic login (CLE-251). */}
              <button
                type="button"
                onClick={handleDemo}
                disabled={submitting}
                className="w-full pt-1 text-center text-sm transition-colors disabled:opacity-50"
                style={{ color: '#94A3B8' }}
              >
                {t('login.demo')}
              </button>

              <p className="text-center text-sm" style={{ color: '#64748B' }}>
                {t('login.noAccount')}{' '}
                <Link to="/register" style={{ color: '#A78BFA' }} className="underline">
                  {t('login.register')}
                </Link>
              </p>
            </form>

            {/* Brand attribution — small "A Clean Code Culture product" line. */}
            <p className="mt-6 text-center text-xs" style={{ color: '#64748B' }}>
              <Link to="/terms" className="transition-colors hover:underline">
                {t('legal.link')}
              </Link>
              {' · '}
              <a
                href={CLEAN_CODE_CULTURE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="transition-colors hover:underline"
              >
                {t('login.poweredBy')}
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
